const pako = require('pako');
const { isTransientNetworkError, readWithRetry, sdkJsonRequest } = require('./riotRequestRecovery');

const PLATFORM = 'ew0KCSJwbGF0Zm9ybVR5cGUiOiAiUEMiLA0KCSJwbGF0Zm9ybU9TIjogIldpbmRvd3MiLA0KCSJwbGF0Zm9ybU9TVmVyc2lvbiI6ICIxMC4wLjE5MDQyLjEuMjU2LjY0Yml0IiwNCgkicGxhdGZvcm1DaGlwc2V0IjogIlVua25vd24iDQp9';
const SHARDS = ['ap', 'na', 'eu', 'kr', 'br', 'latam'];
const LIMIT = 64 * 1024 * 1024;
const EXPIRED_CREDENTIAL_ERRORS = new Set([
    'AUTH_FAILURE', 'AUTHENTICATION_FAILED', 'UNAUTHORIZED',
    'INVALID_TOKEN', 'EXPIRED_TOKEN', 'TOKEN_EXPIRED', 'INVALID_ACCESS_TOKEN', 'ACCESS_TOKEN_EXPIRED',
    'INVALID_SESSION', 'SESSION_EXPIRED', 'RIOT_SESSION_EXPIRED',
]);
let callbackSequence = 0;

function failure(code) { return Object.assign(new Error(code), { code }); }
function object(value) { return value && typeof value === 'object' && !Array.isArray(value); }

function endpointName(url) {
    if (/^https:\/\/auth\.riotgames\.com\/userinfo(?:[?#]|$)/.test(url)) return 'USERINFO';
    if (/^https:\/\/valorant-api\.com\/v1\/version(?:[?#]|$)/.test(url)) return 'VERSION';
    const pd = /^https:\/\/pd\.(?:ap|na|eu|kr|br|latam)\.a\.pvp\.net\/([^/]+)\//.exec(url);
    return pd && ({ 'account-xp': 'ACCOUNT_XP', mmr: 'MMR', 'match-history': 'HISTORY',
        'match-details': 'MATCH', personalization: 'LOADOUT', 'name-service': 'NAMES' })[pd[1]] || 'OTHER';
}
function safeDiagnostic(value = {}, endpoint) {
    value = object(value) ? value : {};
    const endpoints = ['USERINFO', 'VERSION', 'ACCOUNT_XP', 'MMR', 'HISTORY', 'MATCH', 'LOADOUT', 'NAMES', 'OTHER'];
    const bounded = (number, max) => typeof number === 'number' && Number.isFinite(number)
        ? Math.min(max, Math.max(0, Math.floor(number))) : 0;
    return {
        endpoint: endpoints.includes(endpoint || value.endpoint) ? (endpoint || value.endpoint) : 'OTHER',
        callbackReceived: value.callbackReceived === true,
        responseType: ['unreceived', 'undefined', 'null', 'string', 'object', 'array', 'number', 'boolean'].includes(value.responseType)
            ? value.responseType : 'unknown',
        httpStatus: bounded(value.httpStatus, 599), bodyChars: bounded(value.bodyChars, LIMIT),
        elapsedMs: bounded(value.elapsedMs, 180000),
    };
}
function safeLog(level, message, details) {
    try { console[level](message, details); } catch (_) { /* Logging must not change a request outcome. */ }
}

function isRiotCredentialRequest(url, headers) {
    return /^https:\/\/(?:auth\.riotgames\.com|pd\.(?:ap|na|eu|kr|br|latam)\.a\.pvp\.net)(?:\/|$)/i.test(url)
        && Object.keys(headers).some(key => key.toLowerCase() === 'authorization'
            && typeof headers[key] === 'string' && /^Bearer\s+\S+/i.test(headers[key]));
}

// Same native command as Net.send; keep the UTF-8 response text before the SDK parses it.
function nativeRawRequest(bridge, url, method, headers, body, options = {}) {
    const target = options.target || window;
    const startedAt = Date.now();
    const diagnostic = { endpoint: endpointName(url), callbackReceived: false, responseType: 'unreceived', httpStatus: 0, bodyChars: 0 };
    const annotate = error => Object.assign(error, { diagnostic: safeDiagnostic({ ...diagnostic, elapsedMs: Date.now() - startedAt }) });
    return new Promise((resolve, reject) => {
        if (!bridge || typeof bridge.invokeUrlCommand !== 'function') return reject(annotate(failure('RIOT_CLIENT_RAW_UNAVAILABLE')));
        const callback = `riotRecordRaw_${Date.now()}_${++callbackSequence}`;
        let settled = false;
        const finish = (error, value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            // Native requests cannot be aborted. Ignore late native callbacks safely.
            target[callback] = () => {};
            const cleanup = setTimeout(() => { delete target[callback]; }, 60000);
            if (cleanup && typeof cleanup.unref === 'function') cleanup.unref();
            if (error) reject(annotate(error)); else resolve(value);
        };
        const timer = setTimeout(() => finish(failure('RIOT_CLIENT_TIMEOUT')), options.timeout || 15000);
        target[callback] = (encoded, encodedHeaders) => {
            if (settled) return;
            diagnostic.callbackReceived = true;
            diagnostic.responseType = encoded === null ? 'null' : Array.isArray(encoded) ? 'array' : typeof encoded;
            try {
                let responseHeaders = encodedHeaders;
                if (typeof encodedHeaders === 'string') {
                    try { responseHeaders = JSON.parse(decodeURIComponent(encodedHeaders) || '{}'); }
                    catch (_) { responseHeaders = {}; }
                }
                let raw, data = null, bodyError;
                // A missing/undecodable body must not hide an available HTTP error.
                if (typeof encoded !== 'string') bodyError = 'RIOT_CLIENT_RAW_UNAVAILABLE';
                else {
                    try {
                        raw = decodeURIComponent(encoded);
                        diagnostic.bodyChars = raw.length;
                        if (raw.length > 8 * 1024 * 1024) bodyError = 'RIOT_CLIENT_PAYLOAD_INVALID';
                        else { try { data = JSON.parse(raw); } catch (_) { data = null; } }
                    } catch (_) { bodyError = 'RIOT_RESPONSE_INVALID'; }
                }
                const status = Number(responseHeaders && responseHeaders.httpStatus || data && data.httpStatus) || 0;
                diagnostic.httpStatus = status;
                const credentialRequest = isRiotCredentialRequest(url, headers);
                const errorCode = String(data && (data.errorCode || data.error) || '').toUpperCase();
                if (status === 401 && credentialRequest) throw failure('RIOT_SESSION_EXPIRED');
                if (status === 429) throw failure('RIOT_RATE_LIMITED');
                if (status === 404) throw failure('RIOT_NOT_FOUND');
                // Generic 403 and third-party failures do not prove a Riot token expired.
                if ((!status || status === 200 || status === 403) && credentialRequest
                    && EXPIRED_CREDENTIAL_ERRORS.has(errorCode)) throw failure('RIOT_SESSION_EXPIRED');
                if (status && status !== 200) throw failure('RIOT_UPSTREAM_UNAVAILABLE');
                if (bodyError) throw failure(bodyError);
                if (data == null) throw failure('RIOT_RESPONSE_INVALID');
                if (/RATE|TOO_MANY/.test(errorCode)) throw failure('RIOT_RATE_LIMITED');
                if (errorCode) throw failure('RIOT_RESPONSE_INVALID');
                finish(null, { raw, status, data });
            } catch (error) { finish(failure(error.code || 'RIOT_RESPONSE_INVALID')); }
        };
        try {
            const command = { url, method: method.toUpperCase(), contentType: 'application/json',
                headers: Object.keys(headers).map(headerKey => ({ headerKey, headerValue: headers[headerKey] })),
                params: JSON.stringify(body == null ? {} : body), toGetPostBody: null, callback };
            const dispatched = bridge.invokeUrlCommand('gsApp://app.net.send?' + encodeURIComponent(JSON.stringify(command)));
            if (dispatched === false) finish(failure('RIOT_CLIENT_RAW_UNAVAILABLE'));
        } catch (error) { finish(Object.assign(failure('RIOT_UPSTREAM_UNAVAILABLE'), {
            transientNetwork: isTransientNetworkError(error),
        })); }
    });
}

function encodePayload(payload) {
    const text = JSON.stringify(payload);
    if (text.length > LIMIT || new TextEncoder().encode(text).length > LIMIT) throw failure('RIOT_CLIENT_PAYLOAD_INVALID');
    const compressed = pako.gzip(text);
    if (compressed.length > 16 * 1024 * 1024) throw failure('RIOT_CLIENT_PAYLOAD_INVALID');
    let binary = '';
    for (let offset = 0; offset < compressed.length; offset += 8192) {
        binary += String.fromCharCode.apply(null, compressed.subarray(offset, offset + 8192));
    }
    return btoa(binary);
}

async function collectClientResponses(options) {
    const { session, plan } = options;
    if (!session || !/^[A-Za-z0-9-]{1,256}$/.test(session.puuid || '')
        || !session.accessToken || !session.entitlementsToken) throw failure('RIOT_SESSION_EXPIRED');
    const responses = {};
    const now = options.now || Date.now;
    const deadline = now() + 70000;
    let stopped = null;
    let bytes = 0;
    const retryBudget = { remaining: 2 };
    const checkCurrent = () => {
        if (!options.isCurrent()) throw failure('RIOT_CLIENT_CANCELLED');
        if (stopped) throw stopped;
    };
    const check = () => {
        checkCurrent();
        if (now() >= deadline) throw failure('RIOT_CLIENT_TIMEOUT');
    };
    const request = async (key, url, headers, method = 'GET', body = {}, optional = false, archived = true) => {
        check();
        try {
            const read = () => {
                // Optional enrichment must leave time to return already captured core data.
                const timeout = optional ? Math.min(3000, deadline - now() - 5000) : Math.min(15000, deadline - now());
                if (timeout <= 0) throw failure('RIOT_CLIENT_TIMEOUT');
                const transport = !archived && options.jsonRequest ? options.jsonRequest : options.request;
                return transport(url, method, headers, body, timeout);
            };
            // Do not multiply retries across the 50-match detail fan-out or optional enrichment.
            const retryable = !optional && !(key && key.startsWith('match:'));
            const result = retryable ? await readWithRetry(read, {
                check, now, deadline, budget: retryBudget, retryDelayMs: options.retryDelayMs,
            }) : await read();
            if (optional) checkCurrent(); else check();
            if (!result || (archived && typeof result.raw !== 'string')) throw failure('RIOT_CLIENT_RAW_UNAVAILABLE');
            if (key) {
                bytes += result.raw.length;
                if (bytes > LIMIT) throw failure('RIOT_CLIENT_PAYLOAD_INVALID');
                responses[key] = { raw: result.raw, status: result.status || 0 };
            }
            return result.data;
        } catch (error) {
            const code = error.code || 'RIOT_UPSTREAM_UNAVAILABLE';
            const annotated = Object.assign(failure(code), { diagnostic: safeDiagnostic(error.diagnostic, endpointName(url)) });
            if (['RIOT_SESSION_EXPIRED', 'RIOT_RATE_LIMITED', 'RIOT_CLIENT_CANCELLED'].includes(code)) stopped = annotated;
            if (key) responses[key] = { error: code, status: 0 };
            throw annotated;
        }
    };
    const auth = { Authorization: 'Bearer ' + session.accessToken };
    const identity = await request(null, 'https://auth.riotgames.com/userinfo', auth, 'GET', {}, false, false);
    if (!identity || identity.sub !== session.puuid) throw failure('RIOT_ACCOUNT_MISMATCH');
    const version = await request(null, 'https://valorant-api.com/v1/version', {}, 'GET', {}, false, false);
    if (!version || !version.data || !version.data.riotClientVersion) throw failure('RIOT_CLIENT_VERSION_UNAVAILABLE');
    const headers = { ...auth, 'X-Riot-Entitlements-JWT': session.entitlementsToken,
        'X-Riot-ClientPlatform': PLATFORM, 'X-Riot-ClientVersion': version.data.riotClientVersion };
    const puuid = encodeURIComponent(session.puuid);
    let shard, invalidRegionResponse = false;
    for (const region of Array.from(new Set([plan.shard, session.shard, ...SHARDS].filter(value => SHARDS.includes(value))))) {
        try {
            const xp = await request('xp', `https://pd.${region}.a.pvp.net/account-xp/v1/players/${puuid}`, headers);
            if (!object(xp) || xp.Subject !== session.puuid || !object(xp.Progress)) throw failure('RIOT_RESPONSE_INVALID');
            shard = region;
            break;
        } catch (error) {
            check();
            if (error.code !== 'RIOT_NOT_FOUND' && error.code !== 'RIOT_RESPONSE_INVALID') throw error;
            if (error.code === 'RIOT_RESPONSE_INVALID') invalidRegionResponse = true;
        }
    }
    if (!shard) throw failure(invalidRegionResponse ? 'RIOT_RESPONSE_INVALID' : 'RIOT_NOT_FOUND');
    const base = `https://pd.${shard}.a.pvp.net`;
    const mmr = await request('mmr', `${base}/mmr/v1/players/${puuid}`, headers);
    if (!object(mmr) || mmr.Subject !== session.puuid || !object(mmr.QueueSkills)) throw failure('RIOT_RESPONSE_INVALID');
    const history = [];
    for (let start = 0; start < 50; start += 20) {
        let page;
        try {
            page = await request(`history:${start}`, `${base}/match-history/v1/history/${puuid}?startIndex=${start}&endIndex=${Math.min(50, start + 20)}`, headers);
            if (!object(page) || page.Subject !== session.puuid || !Array.isArray(page.History)
                || page.History.length > 20 || !page.History.every(row => row && /^[A-Za-z0-9-]{1,64}$/.test(row.MatchID))) throw failure('RIOT_RESPONSE_INVALID');
        } catch (error) {
            responses[`history:${start}`] = { error: error.code, status: 0 };
            check();
            throw error;
        }
        history.push(...page.History);
        if (!page.History.length || (Number.isFinite(page.Total) && history.length >= page.Total)) break;
    }
    try { await request('loadout', `${base}/personalization/v3/players/${puuid}/playerloadout`, headers, 'GET', {}, true); }
    catch (_) { checkCurrent(); }
    const known = new Set(plan.knownMatchIds || []);
    const ids = Array.from(new Set(history.map(row => row.MatchID))).filter(id => !known.has(id)).slice(0, 50);
    const subjects = new Set();
    let next = 0;
    async function worker() {
        while (next < ids.length) {
            check();
            const id = ids[next++];
            try {
                const detail = await request(`match:${id}`, `${base}/match-details/v1/matches/${id}`, headers);
                if (!detail || !detail.matchInfo || detail.matchInfo.matchId !== id || !Array.isArray(detail.players)
                    || !detail.players.some(player => player && player.subject === session.puuid)) throw failure('RIOT_RESPONSE_INVALID');
                detail.players.forEach(player => {
                    if (player && player.subject && !player.gameName && !player.displayName) subjects.add(player.subject);
                });
            } catch (error) { responses[`match:${id}`] = { error: error.code, status: 0 }; check(); }
        }
    }
    await Promise.all(Array.from({ length: Math.min(4, ids.length) }, worker));
    check();
    const names = [];
    (plan.nameSubjects || []).forEach(value => {
        if (/^[A-Za-z0-9-]{1,256}$/.test(value)) subjects.add(value);
    });
    const allSubjects = Array.from(subjects).slice(0, 2000);
    for (let offset = 0; offset < allSubjects.length; offset += 50) {
        checkCurrent();
        if (deadline - now() <= 5000) break;
        const batch = allSubjects.slice(offset, offset + 50);
        try {
            const result = await request(null, `${base}/name-service/v2/players`, headers, 'PUT', batch, true);
            if (Array.isArray(result)) result.forEach((row, index) => {
                if (object(row)) names.push({ ...row, Subject: row.Subject || batch[index] });
            });
        } catch (_) { checkCurrent(); }
    }
    responses.names = { raw: JSON.stringify(names), status: 0 };
    checkCurrent();
    return encodePayload({ version: 1, shard, responses });
}

// Cache reads never enter this function. An explicit failure is reported, not rerouted to server PD.
async function prepareClientRequest(api, bridge, params, options) {
    const startedAt = Date.now();
    const target = options.target || window;
    let leftPage = false;
    const onHide = () => { leftPage = true; };
    const isCurrent = () => !leftPage && options.isCurrent();
    target.addEventListener('pagehide', onHide);
    try {
        const planDeadline = Date.now() + 10000;
        const response = await readWithRetry(async () => {
            let planTimer;
            try {
                return await Promise.race([
                    api.getRiotClientPlan({ gamerskyUserId: params.gamerskyUserId,
                        bindInfoId: params.bindInfoId, puuid: params.puuid }),
                    new Promise((_, reject) => { planTimer = setTimeout(() => reject(failure('RIOT_CLIENT_TIMEOUT')),
                        Math.max(1, planDeadline - Date.now())); }),
                ]);
            } finally { clearTimeout(planTimer); }
        }, { deadline: planDeadline, retryDelayMs: options.retryDelayMs,
            check: () => { if (!isCurrent()) throw failure('RIOT_CLIENT_CANCELLED'); },
        }).catch(error => {
            if (error.code === 'RIOT_CLIENT_CANCELLED') throw error;
            throw failure('RIOT_CLIENT_PLAN_FAILED');
        });
        if (!isCurrent()) throw failure('RIOT_CLIENT_CANCELLED');
        if (!response || response.error || response.code !== 0 || !object(response.data)) throw failure('RIOT_CLIENT_PLAN_FAILED');
        if (params.deferActivation === true && response.data.supportsDeferredActivation !== true) {
            throw failure('RIOT_QUICK_BIND_UNSUPPORTED');
        }
        if (params.bindInfoId && response.data.canRefresh === false) return { ...params, update: false };
        const planElapsedMs = Date.now() - startedAt;
        const clientPayload = await collectClientResponses({ session: params, plan: response.data, isCurrent,
            retryDelayMs: options.retryDelayMs,
            jsonRequest: bridge && bridge.Net && typeof bridge.Net.send === 'function'
                ? (url, method, headers, body, timeout) => sdkJsonRequest(bridge, url, method, headers, body, { timeout, isCurrent })
                : undefined,
            request: (url, method, headers, body, timeout) => nativeRawRequest(bridge, url, method, headers, body, { target, timeout }) });
        if (!isCurrent()) throw failure('RIOT_CLIENT_CANCELLED');
        safeLog('info', '[ValorantRiotClientFetch] capture completed (not yet saved)', {
            planElapsedMs, captureElapsedMs: Date.now() - startedAt - planElapsedMs,
            clientElapsedMs: Date.now() - startedAt, payloadChars: clientPayload.length,
        });
        return { ...params, clientPayload, clientElapsedMs: Date.now() - startedAt };
    } catch (error) {
        if (!isCurrent() || error.code === 'RIOT_CLIENT_CANCELLED') throw failure('RIOT_CLIENT_CANCELLED');
        safeLog('warn', '[ValorantRiotClientFetch] capture failed', {
            code: error.code || 'RIOT_CLIENT_PLAN_FAILED', clientElapsedMs: Date.now() - startedAt,
            traceId: /^riot-[a-z-]*\d{13}-[a-f0-9]+$/.test(params.monitorTraceId || '') ? params.monitorTraceId : undefined,
            diagnostic: error.diagnostic ? safeDiagnostic(error.diagnostic) : undefined,
        });
        return { ...params, clientError: error.code || 'RIOT_CLIENT_PLAN_FAILED', clientElapsedMs: Date.now() - startedAt };
    } finally { target.removeEventListener('pagehide', onHide); }
}

async function submitClientRequest(submit, params) {
    const startedAt = Date.now();
    const clientElapsedMs = Math.max(0, Number(params.clientElapsedMs) || 0);
    try {
        const response = await submit(params);
        console.info('[ValorantRiotClientFetch] server response', {
            traceId: params.monitorTraceId,
            success: Boolean(response && !response.error && response.riotStats && response.riotStats.view),
            clientElapsedMs, roundTripMs: Date.now() - startedAt,
            totalElapsedMs: clientElapsedMs + Date.now() - startedAt,
        });
        return response;
    } catch (error) {
        console.warn('[ValorantRiotClientFetch] server response unconfirmed', {
            traceId: params.monitorTraceId, clientElapsedMs, roundTripMs: Date.now() - startedAt,
        });
        throw error;
    }
}

module.exports = { collectClientResponses, nativeRawRequest, prepareClientRequest, submitClientRequest, encodePayload };
